/**
 * L'écriture d'un Move, telle qu'on l'écrit au tableau : « MN ÷ BC = AM ÷ AB » se lit en FRACTIONS
 * empilées, pas avec des « ÷ » en ligne (retour de Philippe du 5/11/2026). On rend le texte du
 * catalogue (`writes`, `choices[].text` — la même langue que le moteur) en LaTeX pour `MathText` ;
 * si quoi que ce soit résiste, on renvoie le texte tel quel, et il s'affiche comme avant.
 *
 * ⚠️ Ce n'est PAS un lecteur mathématique : juste de quoi écrire les formules du catalogue. Il ne
 * sert jamais à juger quoi que ce soit — le moteur, lui, lit le texte d'origine.
 */

type Node =
  | { k: 'num' | 'id' | 'raw'; v: string }
  | { k: 'op'; v: string; a: Node; b: Node }
  | { k: 'neg' | 'sqrt' | 'paren'; a: Node }
  | { k: 'sup'; a: Node; v: string };

const SUP: Record<string, string> = { '²': '2', '³': '3', '⁴': '4', ⁿ: 'n' };
const SUB: Record<string, string> = { '₀': '0', '₁': '1', '₂': '2', '₃': '3', ₙ: 'n', ₖ: 'k' };
/** lettres grecques et symboles que KaTeX écrit mieux par leur commande */
const NAMES: Record<string, string> = {
  Δ: '\\Delta',
  ℓ: '\\ell',
  π: '\\pi',
  α: '\\alpha',
  β: '\\beta',
  θ: '\\theta',
  '°': '^{\\circ}',
};
const RELS = ['=', '≤', '≥', '<', '>', '≠', '≈'];
const ADD = ['+', '−', '-', '±'];
const MUL = ['×', '÷', '·'];

/** Découpe en atomes : nombre, nom (avec indices, exposants, flèche de vecteur), signe, parenthèse. */
function lex(text: string): string[] | null {
  const out: string[] = [];
  let s = text.trim();
  while (s.length) {
    if (/^\s/u.test(s)) {
      s = s.replace(/^\s+/u, '');
      continue;
    }
    const m =
      /^\d+(?:[,.]\d+)?/u.exec(s) ??
      /^[\p{L}ℓΔ][\p{L}ℓΔ]*⃗?[₀-₉ₙₖ]*/u.exec(s) ??
      /^[()√+−\-±×÷·=≤≥<>≠≈°²³⁴ⁿ]/u.exec(s);
    if (!m) return null;
    out.push(m[0]);
    s = s.slice(m[0].length);
  }
  return out.length ? out : null;
}

/** Un nom : « AB », « x₁ », « u⃗ », « Δ ». */
function name(a: string): string {
  if (NAMES[a]) return NAMES[a];
  const vec = a.includes('⃗');
  let v = a.replace('⃗', '');
  let sub = '';
  while (v.length > 1 && SUB[v[v.length - 1]!]) {
    sub = SUB[v[v.length - 1]!]! + sub;
    v = v.slice(0, -1);
  }
  const base = NAMES[v] ?? (v.length > 1 ? `\\mathit{${v}}` : v);
  const core = vec ? `\\vec{${base}}` : base;
  return sub ? `${core}_{${sub}}` : core;
}

class P {
  i = 0;
  constructor(private t: string[]) {}
  peek() {
    return this.t[this.i];
  }
  eat(v: string) {
    if (this.t[this.i] === v) return !!++this.i;
    return false;
  }
  rel(): Node | null {
    let a = this.add();
    if (!a) return null;
    while (this.peek() && RELS.includes(this.peek()!)) {
      const v = this.t[this.i++]!;
      const b = this.add();
      if (!b) return null;
      a = { k: 'op', v, a, b };
    }
    return a;
  }
  add(): Node | null {
    let a = this.mul();
    if (!a) return null;
    while (this.peek() && ADD.includes(this.peek()!)) {
      const v = this.t[this.i++]!;
      const b = this.mul();
      if (!b) return null;
      a = { k: 'op', v, a, b };
    }
    return a;
  }
  mul(): Node | null {
    let a = this.unary();
    if (!a) return null;
    for (;;) {
      const v = this.peek();
      if (v && MUL.includes(v)) {
        this.i++;
        const b = this.unary();
        if (!b) return null;
        a = { k: 'op', v, a, b };
        continue;
      }
      // produit sous-entendu : « 2a », « ka »
      if (v && !RELS.includes(v) && !ADD.includes(v) && v !== ')' && this.atomStart(v)) {
        const b = this.unary();
        if (!b) return null;
        a = { k: 'op', v: '', a, b };
        continue;
      }
      return a;
    }
  }
  atomStart(v: string) {
    return /^[\p{L}\d(√ℓΔ]/u.test(v);
  }
  unary(): Node | null {
    if (this.eat('−') || this.eat('-')) {
      const a = this.unary();
      return a && { k: 'neg', a };
    }
    if (this.eat('√')) {
      const a = this.unary();
      return a && { k: 'sqrt', a };
    }
    return this.sup(this.atom());
  }
  sup(a: Node | null): Node | null {
    while (a && this.peek() && SUP[this.peek()!]) a = { k: 'sup', a, v: SUP[this.t[this.i++]!]! };
    if (a && this.peek() === '°') {
      this.i++;
      a = { k: 'sup', a, v: '\\circ' };
    }
    return a;
  }
  atom(): Node | null {
    const v = this.peek();
    if (!v) return null;
    if (v === '(') {
      this.i++;
      const a = this.rel();
      if (!a || !this.eat(')')) return null;
      return { k: 'paren', a };
    }
    if (/^\d/u.test(v)) return (this.i++, { k: 'num', v });
    if (/^[\p{L}ℓΔ]/u.test(v)) return (this.i++, { k: 'id', v });
    return null;
  }
}

/** Le LaTeX d'un nœud. `bare` : on est seul dans une case (numérateur, racine) — les parenthèses tombent. */
function tex(n: Node, bare = false): string {
  switch (n.k) {
    case 'num':
      return n.v.replace(',', '{,}');
    case 'id':
      return name(n.v);
    case 'raw':
      return n.v;
    case 'neg':
      return `-${tex(n.a)}`;
    case 'sqrt':
      return `\\sqrt{${tex(n.a, true)}}`;
    case 'sup':
      return `${tex(n.a)}^{${n.v}}`;
    case 'paren':
      return bare ? tex(n.a, true) : `\\left(${tex(n.a)}\\right)`;
    case 'op':
      if (n.v === '÷') return `\\frac{${tex(n.a, true)}}{${tex(n.b, true)}}`;
      if (n.v === '') return `${tex(n.a)}${tex(n.b)}`;
      if (n.v === '×' || n.v === '·') return `${tex(n.a)} \\times ${tex(n.b)}`;
      if (n.v === '−' || n.v === '-') return `${tex(n.a)} - ${tex(n.b)}`;
      if (n.v === '±') return `${tex(n.a)} \\pm ${tex(n.b)}`;
      if (n.v === '≤') return `${tex(n.a)} \\le ${tex(n.b)}`;
      if (n.v === '≥') return `${tex(n.a)} \\ge ${tex(n.b)}`;
      if (n.v === '≠') return `${tex(n.a)} \\ne ${tex(n.b)}`;
      if (n.v === '≈') return `${tex(n.a)} \\approx ${tex(n.b)}`;
      return `${tex(n.a)} ${n.v} ${tex(n.b)}`;
  }
}

/**
 * L'écriture d'un Move, prête pour `MathText` : `$…$` quand elle se lit, le texte d'origine sinon.
 * Un texte qui est une PHRASE (le « pourquoi » d'un geste) n'est jamais touché.
 */
export function moveMath(text: string | undefined): string {
  if (!text) return '';
  if (/[a-zà-ÿ]{4,}\s/iu.test(text) || text.length > 60) return text;
  const t = lex(text);
  if (!t) return text;
  const p = new P(t);
  const n = p.rel();
  if (!n || p.i < t.length) return text;
  return `$${tex(n)}$`;
}
